import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { playApi } from '../services/api';
import { releaseVelocity } from './physics';
import { aimTarget } from './aim';
import { MountainMark, Puck } from './Art';
import './game.css';

const LINES = {
  plate: { name: 'Supermont Plate', tagline: "L'eau pure de nos montagnes" },
  petillante: { name: 'Supermont La Pétillante', tagline: 'Des bulles fines, tout en élégance' },
};
// Six cibles : rangée lointaine (0, 1, 2) puis rangée proche (3, 4, 5).
const BOX_LABELS = [
  'Cible lointaine à gauche', 'Cible lointaine au centre', 'Cible lointaine à droite',
  'Cible proche à gauche', 'Cible proche au centre', 'Cible proche à droite',
];
const TARGETS = [0, 1, 2, 3, 4, 5];
const FAR = (i) => i < 3;
const TRAVEL_MS = 560;
const OPEN_MS = 720;

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const pendingKey = (token) => `palet_pending_${token}`;
function readPending(token) {
  try {
    const v = sessionStorage.getItem(pendingKey(token));
    return v === null ? null : JSON.parse(v);
  } catch {
    return null;
  }
}
function writePending(token, value) {
  try {
    if (value === null) sessionStorage.removeItem(pendingKey(token));
    else sessionStorage.setItem(pendingKey(token), JSON.stringify(value));
  } catch { /* stockage indisponible : sans incidence */ }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Image du lot : « {gamme} » dans le chemin est remplacé par la gamme du jeu
// (visuel La Source pour Plate, La Pétillante pour Pétillante) ; si ce visuel
// n'existe pas, on essaie l'autre gamme, puis on n'affiche rien.
function PrizeImage({ icon, line }) {
  const other = line === 'petillante' ? 'plate' : 'petillante';
  const candidates = icon.includes('{gamme}')
    ? [icon.replace('{gamme}', line), icon.replace('{gamme}', other)]
    : [icon];
  const [index, setIndex] = useState(0);
  if (index >= candidates.length) return null;
  return (
    <img className="result-img" src={candidates[index]} alt="" onError={() => setIndex((i) => i + 1)} />
  );
}

export default function Play() {
  // Le lien peut désigner un achat (plusieurs lancers) ou un lancer précis ;
  // `token` est toujours le jeton du lancer en cours, seul utilisé pour le choix.
  const { token: linkToken } = useParams();
  const [token, setToken] = useState(linkToken);
  const [series, setSeries] = useState({ number: 1, total: 1, hasNext: false, plays: [] });
  const [reloadKey, setReloadKey] = useState(0);
  const [phase, setPhase] = useState('loading'); // loading | ready | travel | waiting | opening | result | closed
  const [line, setLine] = useState('plate');
  const [code, setCode] = useState(null);
  const [aim, setAim] = useState(null);           // case visée pendant le geste
  const [target, setTarget] = useState(null);     // case choisie (verrouillée)
  const [drag, setDrag] = useState(null);         // décalage du palet pendant le geste
  const [travel, setTravel] = useState(null);     // translation vers la case
  const [result, setResult] = useState(null);
  const [notice, setNotice] = useState(null);     // message non bloquant
  const [closed, setClosed] = useState(null);     // écran bloquant (annulée, introuvable…)
  const [netTrouble, setNetTrouble] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [live, setLive] = useState('');

  const boardRef = useRef(null);
  const puckRef = useRef(null);
  const boxRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];
  const lockRef = useRef(false);
  const gestureRef = useRef(null);
  const geomRef = useRef(null);
  const mounted = useRef(true);
  const reduce = useMemo(reducedMotion, []);
  const hasHostess = typeof localStorage !== 'undefined' && !!localStorage.getItem('palet_token');

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', line === 'petillante' ? '#07402a' : '#0a2f6b');
  }, [line]);

  // ------------------------------------------------------------------ géométrie
  const measure = useCallback(() => {
    const board = boardRef.current;
    const puck = puckRef.current;
    if (!board || !puck || boxRefs.some((r) => !r.current)) return null;
    const b = board.getBoundingClientRect();
    const rects = boxRefs.map((r) => {
      const rc = r.current.getBoundingClientRect();
      return { x0: rc.left - b.left, y0: rc.top - b.top, x1: rc.right - b.left, y1: rc.bottom - b.top };
    });
    // Position de repos du palet (offset* ignore la translation en cours).
    const r = puck.offsetWidth / 2;
    geomRef.current = {
      board: b,
      puck: { x: puck.offsetLeft + r, y: puck.offsetTop + r },
      puckR: r,
      rects,
      boxes: rects.map((c) => ({ x: (c.x0 + c.x1) / 2, y: (c.y0 + c.y1) / 2 })),
      walls: { left: b.width * 0.08 + r * 0.6, right: b.width * 0.92 - r * 0.6, top: r * 0.5, bottom: puck.offsetTop + r },
    };
    return geomRef.current;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, phase]);

  // ------------------------------------------------------------------ résultat
  const showResult = useCallback(async (data, box, { animate, tk }) => {
    writePending(tk || token, null);
    setLine(data.line || 'plate');
    setCode(data.code);
    setTarget(box);
    setResult(data);
    if (animate && !reduce) {
      setPhase('opening');
      await sleep(OPEN_MS);
      if (!mounted.current) return;
    }
    setPhase('result');
    setLive(data.is_winner
      ? `Bravo ! Vous gagnez : ${data.prize?.label}. Code ${data.code}.`
      : `Merci d'avoir joué. Code ${data.code}.`);
  }, [token, reduce]);

  // ------------------------------------------------------------------ envoi du choix
  const sendChoice = useCallback(async (box, input, tk) => {
    // Relances automatiques sur coupure réseau : on renvoie TOUJOURS la même case.
    // Le serveur est idempotent : si le premier envoi était passé, il renvoie ce résultat.
    const delays = [0, 800, 1600, 3200, 5000];
    for (let i = 0; i < delays.length; i += 1) {
      if (delays[i]) await sleep(delays[i]);
      if (!mounted.current) return null;
      try {
        const data = await playApi.choose(tk || token, box, input);
        setNetTrouble(false);
        return data;
      } catch (e) {
        if (!e.network && !(e.status >= 500)) throw e;
        setNetTrouble(true);
      }
    }
    const err = new Error('network');
    err.network = true;
    throw err;
  }, [token]);

  const handleChoiceError = useCallback(async (e) => {
    if (e.network) {
      setPhase('waiting');
      setNetTrouble(true);
      return;
    }
    writePending(token, null);
    if (e.code === 'RESERVATION_EXPIREE') {
      // Les cases ont été renouvelées avant tout choix enregistré : on relance.
      lockRef.current = false;
      setTarget(null);
      setTravel(null);
      setNotice('La partie a été renouvelée. Relancez le palet.');
      setPhase('loading');
      try {
        const st = await playApi.state(token);
        if (st.status === 'open') setPhase('ready');
        else if (st.status === 'unavailable') setClosed({ title: 'Jeu indisponible', text: st.message });
        else await showResult(st, st.chosen_box, { animate: false });
      } catch {
        setClosed({ title: 'Connexion perdue', text: 'Rechargez la page pour reprendre la partie.' });
      }
      return;
    }
    if (e.code === 'PARTICIPATION_ANNULEE') {
      setClosed({ title: 'Participation annulée', text: "Adressez-vous à l'hôtesse." });
      return;
    }
    setClosed({ title: 'Partie impossible', text: e.message || "Adressez-vous à l'hôtesse." });
  }, [token, showResult]);

  const choose = useCallback(async (box, input, landing) => {
    if (lockRef.current || phase !== 'ready') return;
    lockRef.current = true; // double appui / double geste : un seul envoi
    writePending(token, { box, input });
    setAim(null);
    setDrag(null);
    setTarget(box);
    setLive(`Le palet s'arrête sur la ${BOX_LABELS[box].toLowerCase()}.`);

    const g = geomRef.current || measure();
    let ms = TRAVEL_MS;
    if (g) {
      // Le palet glisse depuis l'endroit où le doigt l'a lâché, dans la direction du geste.
      const to = landing || g.boxes[box];
      const from = landing ? landing.from : g.puck;
      ms = Math.round(Math.min(900, Math.max(420, Math.hypot(to.x - from.x, to.y - from.y) * 2.2)));
      setTravel({ x: to.x - g.puck.x, y: to.y - g.puck.y, s: FAR(box) ? 0.5 : 0.66, ms });
    }
    const moving = sleep(reduce ? 0 : ms);
    setPhase('travel');

    const request = sendChoice(box, input);
    await Promise.all([moving, request.catch(() => null)]);
    if (!mounted.current) return;
    try {
      const data = await request;
      if (data) await showResult(data, box, { animate: true });
    } catch (e) {
      await handleChoiceError(e);
    }
  }, [phase, token, measure, sendChoice, reduce, showResult, handleChoiceError]);

  const retry = useCallback(async () => {
    const pending = readPending(token);
    if (!pending) return;
    setNetTrouble(false);
    setPhase('travel');
    try {
      const data = await sendChoice(pending.box, pending.input);
      if (data) await showResult(data, pending.box, { animate: true });
    } catch (e) {
      await handleChoiceError(e);
    }
  }, [token, sendChoice, showResult, handleChoiceError]);

  // ------------------------------------------------------------------ chargement
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
          const st = await playApi.state(linkToken);
          if (cancelled) return;
          const rt = st.round_token || linkToken;
          setToken(rt);
          setSeries({ number: st.play_number || 1, total: st.plays_total || 1, hasNext: !!st.has_next, plays: st.plays || [] });
          setLine(st.line || 'plate');
          setCode(st.code);
          if (st.status === 'open') {
            const pending = readPending(rt);
            if (pending) {
              // Rechargement pendant l'envoi : on renvoie le même choix, jamais un autre.
              lockRef.current = true;
              setTarget(pending.box);
              setPhase('waiting');
              try {
                const data = await sendChoice(pending.box, pending.input, rt);
                if (data && !cancelled) await showResult(data, pending.box, { animate: true, tk: rt });
              } catch (e) {
                if (!cancelled) await handleChoiceError(e);
              }
            } else {
              setPhase('ready');
            }
          } else if (st.status === 'cancelled') {
            setClosed({ title: 'Participation annulée', text: "Adressez-vous à l'hôtesse." });
          } else if (st.status === 'unavailable') {
            setClosed({ title: 'Jeu momentanément indisponible', text: st.message });
          } else {
            await showResult(st, st.chosen_box, { animate: false, tk: rt });
          }
          return;
        } catch (e) {
          if (cancelled) return;
          if (e.status === 404) {
            setClosed({ title: 'Lien inconnu', text: "Ce lien de jeu n'existe pas. Adressez-vous à l'hôtesse." });
            return;
          }
          await sleep(1000 * (attempt + 1));
        }
      }
      if (!cancelled) setClosed({ title: 'Connexion impossible', text: 'Vérifiez le réseau puis rechargez la page.', reload: true });
    })();
    return () => { cancelled = true; };
  }, [linkToken, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lancer suivant du même achat : nouvel état, nouvelles cases (tirées à ce moment-là).
  const nextThrow = useCallback(() => {
    lockRef.current = false;
    setResult(null);
    setTarget(null);
    setTravel(null);
    setAim(null);
    setDrag(null);
    setNotice(null);
    setInteracted(false);
    setPhase('loading');
    setReloadKey((k) => k + 1);
  }, []);

  // Récapitulatif des lancers d'un achat (le lancer affiché compte avec son résultat).
  const summary = series.plays.map((p) => (p.play_number === series.number && result
    ? { ...p, status: result.status, is_winner: result.is_winner, prize_label: result.prize?.label || null }
    : p));

  // ------------------------------------------------------------------ geste
  const onPointerDown = (e) => {
    if (phase !== 'ready' || lockRef.current) return;
    if (e.button !== undefined && e.button !== 0) return;
    const g = measure();
    if (!g) return;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* pointeur déjà relâché */ }
    const at = { x: e.clientX - g.board.left, y: e.clientY - g.board.top, t: performance.now() };
    gestureRef.current = { id: e.pointerId, start: at, samples: [at] };
    setInteracted(true);
    setNotice(null);
  };

  // Le palet suit le doigt dans la zone de lancer (sous la rangée proche).
  const clampDrag = (g, dx, dy) => {
    const nearBottom = Math.max(...g.rects.map((r) => r.y1));
    return {
      x: Math.max(g.walls.left - g.puck.x, Math.min(g.walls.right - g.puck.x, dx)),
      y: Math.max(nearBottom + g.puckR - g.puck.y, Math.min(g.puckR * 0.3, dy)),
    };
  };

  const onPointerMove = (e) => {
    const gs = gestureRef.current;
    const g = geomRef.current;
    if (!gs || gs.id !== e.pointerId || !g) return;
    const cur = { x: e.clientX - g.board.left, y: e.clientY - g.board.top, t: performance.now() };
    gs.samples.push(cur);
    if (gs.samples.length > 24) gs.samples.shift();
    setDrag(clampDrag(g, cur.x - gs.start.x, cur.y - gs.start.y));
  };

  const endGesture = (e, cancelled) => {
    const gs = gestureRef.current;
    const g = geomRef.current;
    gestureRef.current = null;
    if (!gs || !g) return;
    const cur = { x: e.clientX - g.board.left, y: e.clientY - g.board.top, t: performance.now() };
    gs.samples.push(cur);
    const v = releaseVelocity(gs.samples);
    const aimRes = cancelled ? null : aimTarget(gs.start, cur, g.rects, g.puck, Math.max(0, -v.vy));
    window.__paletLastThrow = { v, box: aimRes ? aimRes.index : null };
    if (!aimRes) {
      setDrag(null);
      if (!cancelled) setNotice('Glissez le palet vers le haut, en direction des cibles.');
      return;
    }
    const off = clampDrag(g, cur.x - gs.start.x, cur.y - gs.start.y);
    choose(aimRes.index, 'swipe', { ...aimRes.point, from: { x: g.puck.x + off.x, y: g.puck.y + off.y } });
  };

  // Clavier : ← → dans la rangée, ↑ ↓ pour changer de rangée, Entrée / Espace pour lancer.
  const onPuckKey = (e) => {
    if (phase !== 'ready') return;
    const cur = aim ?? 4;
    const col = cur % 3;
    const base = cur - col;
    const move = { ArrowLeft: base + Math.max(0, col - 1), ArrowRight: base + Math.min(2, col + 1), ArrowUp: col, ArrowDown: 3 + col }[e.key];
    if (move !== undefined) { e.preventDefault(); setAim(move); setInteracted(true); }
    else if (['Enter', ' '].includes(e.key)) { e.preventDefault(); choose(cur, 'keyboard'); }
  };

  useEffect(() => {
    if (aim !== null && phase === 'ready' && !gestureRef.current) setLive(`Visée : ${BOX_LABELS[aim].toLowerCase()}.`);
  }, [aim, phase]);

  // ------------------------------------------------------------------ rendu
  const theme = LINES[line] || LINES.plate;
  const puckStyle = travel && phase !== 'ready'
    ? { transform: `translate(${travel.x}px, ${travel.y}px) scale(${travel.s ?? 0.62})`, transitionDuration: reduce ? '0ms' : `${travel.ms || TRAVEL_MS}ms` }
    : drag
      ? { transform: `translate(${drag.x}px, ${drag.y}px)`, transitionDuration: '0ms' }
      : undefined;

  return (
    <div className={`game line-${line} phase-${phase}${reduce ? ' reduce' : ''}`}>
      <div className="game-bg" aria-hidden="true">
        <div className="caustic c1" />
        <div className="caustic c2" />
        {line === 'petillante' && (
          <div className="bubbles">
            {Array.from({ length: 14 }, (_, i) => (
              <span key={i} style={{ '--i': i, '--s': `${3 + ((i * 7) % 5)}px`, '--d': `${7 + ((i * 13) % 6)}s` }} />
            ))}
          </div>
        )}
      </div>

      <header className="game-head">
        <div className="brand-chip"><img src="/images/logo-supermont.png" alt="Supermont" /></div>
        <p className="line-name">{theme.name}</p>
        {series.total > 1 && <p className="series-chip">Lancer {series.number} / {series.total}</p>}
      </header>

      {closed ? (
        <main className="closed-card" role="alert">
          <h1>{closed.title}</h1>
          <p>{closed.text}</p>
          {code && <p className="code-small">Participation {code}</p>}
          {closed.reload && <button className="btn-light" onClick={() => window.location.reload()}>Recharger</button>}
          {hasHostess && <Link className="hostess-link" to="/hotesse">Espace hôtesse</Link>}
        </main>
      ) : (
        <main className="game-main">
          <h1 className="instruction" aria-live="off">
            {{
              loading: 'Préparation…',
              travel: 'Le palet glisse…',
              waiting: 'Le palet glisse…',
              opening: 'La cible s’ouvre…',
              result: 'Votre cible est ouverte',
            }[phase] || 'Glissez le palet vers une cible'}
          </h1>

          <div
            className="board"
            ref={boardRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={(e) => endGesture(e, false)}
            onPointerCancel={(e) => endGesture(e, true)}
          >
            <svg className="lane" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              <polygon points="14,2 86,2 76,97 24,97" className="lane-surface" />
              <line x1="9" y1="29" x2="91" y2="29" className="row-line" vectorEffect="non-scaling-stroke" />
            </svg>

            <div className="boxes" role="group" aria-label="Six cibles fermées : trois proches, trois lointaines">
              {TARGETS.map((i) => {
                const chosen = target === i && (phase === 'opening' || phase === 'result');
                return (
                  <div
                    key={i}
                    ref={boxRefs[i]}
                    role="img"
                    className={`box pos-${i} ${FAR(i) ? 'far' : 'near'}${aim === i ? ' aimed' : ''}${chosen ? ' open' : ''}${target !== null && target !== i && phase === 'result' ? ' dim' : ''}`}
                    aria-label={`${BOX_LABELS[i]}${chosen && result ? ` : ${result.prize?.label || ''}` : ''}`}
                  >
                    <span className="box-glow" aria-hidden="true" />
                    <span className="box-inner" aria-hidden="true">
                      {chosen && result && (
                        <span className={`box-prize${result.is_winner ? ' win' : ''}`}>
                          {result.is_winner ? '★' : '✓'}
                        </span>
                      )}
                    </span>
                    <span className="box-lid" aria-hidden="true">
                      <MountainMark />
                      <span className="box-q">?</span>
                    </span>
                    {chosen && !reduce && (
                      <span className="ripples" aria-hidden="true"><i /><i /><i /></span>
                    )}
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              ref={puckRef}
              className={`puck${phase === 'ready' && !interacted ? ' hint' : ''}`}
              style={puckStyle}
              aria-label="Palet. Flèches pour viser (haut et bas pour changer de rangée), Entrée pour lancer."
              disabled={phase !== 'ready'}
              onKeyDown={onPuckKey}
            >
              <Puck />
            </button>
            <div className="puck-shadow" aria-hidden="true" />
            {phase === 'ready' && !interacted && <div className="swipe-hint" aria-hidden="true" />}
          </div>

          <div className="under-board">
            {netTrouble ? (
              <div className="net" role="status">
                <span className="spinner" aria-hidden="true" />
                Connexion instable… votre choix ({BOX_LABELS[target ?? 4].toLowerCase()}) est conservé.
                {phase === 'waiting' && <button className="btn-light" onClick={retry}>Réessayer</button>}
              </div>
            ) : notice ? (
              <p className="notice" role="status">{notice}</p>
            ) : (
              <p className="subtle">La direction et la force de votre geste décident de la cible.</p>
            )}
          </div>
        </main>
      )}

      {phase === 'result' && result && (
        <section className={`result ${result.is_winner ? 'win' : 'lose'}`} aria-labelledby="result-title">
          <div className="result-card">
            <p className="result-kicker">{result.is_winner ? 'Bravo !' : 'Pas de lot cette fois'}</p>
            <h2 id="result-title" className="result-prize">
              {result.is_winner ? result.prize?.label : (result.prize?.label || 'Pas de lot cette fois')}
            </h2>
            {result.is_winner && result.prize?.icon && <PrizeImage icon={result.prize.icon} line={line} />}
            {result.prize?.description && result.is_winner && <p className="result-desc">{result.prize.description}</p>}
            <div className="result-code">
              <span>Participation</span>
              <strong>{result.code}</strong>
            </div>
            {result.delivered ? (
              <p className="result-status done">Lot remis{result.delivered_at ? ` le ${new Date(result.delivered_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}` : ''}.</p>
            ) : result.is_winner ? (
              <p className="result-status">Montrez cet écran à l’hôtesse pour recevoir votre lot.</p>
            ) : (
              <p className="result-status">Présentez ce code à l’hôtesse si besoin.</p>
            )}
            {result.already_played && !result.delivered && (
              <p className="result-note">Cette participation a déjà été jouée : voici son résultat.</p>
            )}
            {series.hasNext ? (
              <button type="button" className="btn-next" onClick={nextThrow}>
                Lancer suivant ({series.number + 1}/{series.total})
              </button>
            ) : series.total > 1 && (
              <ul className="result-summary" aria-label="Vos lancers">
                {summary.map((p) => (
                  <li key={p.play_number} className={p.is_winner ? 'win' : ''}>
                    <span>Lancer {p.play_number}</span>
                    <strong>{p.prize_label || (p.status === 'open' ? 'à jouer' : p.status === 'cancelled' ? 'annulé' : '—')}</strong>
                  </li>
                ))}
              </ul>
            )}
            {hasHostess && <Link className="hostess-link" to="/hotesse">Espace hôtesse</Link>}
          </div>
        </section>
      )}

      <div className="sr-only" aria-live="polite">{live}</div>
    </div>
  );
}
